import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { JwtHelperService } from '@auth0/angular-jwt';

import { JwtUser } from '@models/authentication/jwt-user.model';

import { Role } from '@models/authentication/enums/role.enum';

import { AppComponent } from '@app/app.component';

import { of } from 'rxjs';

import { environment } from 'environments/environment';

/**
 * Service for handling authentication operations, and for retrieving decrypted information
 * about the current user from the stored JWT.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  static readonly USER_TOKEN_KEY = 'user_token';
  static readonly API_LOGIN_BASE_PATH = `${environment.api.rest.auth}/login`;
  static readonly API_REFRESH_TOKEN_PATH = `${environment.api.rest.auth}/token`;
  static readonly API_FORGOT_PASSWORD_PATH = `${environment.api.rest.auth}/forgot-password`;
  static readonly API_RESET_PASSWORD_PATH = `${environment.api.rest.auth}/reset-password`;

  constructor(private http: HttpClient, private jwtHelper: JwtHelperService) {}

  login(payload) {
    return this.http.post(`${AuthenticationService.API_LOGIN_BASE_PATH}`, payload);
  }

  refreshToken() {
    return this.http.get(`${AuthenticationService.API_REFRESH_TOKEN_PATH}`);
  }

  storeToken(token: string): void {
    localStorage.setItem(AuthenticationService.USER_TOKEN_KEY, token);
  }

  logout() {
    localStorage.removeItem(AuthenticationService.USER_TOKEN_KEY);
    localStorage.removeItem(AppComponent.LANGUAGE_KEY);
    return of(true);
  }

  forgotPassword(email: string) {
    return this.http.post(AuthenticationService.API_FORGOT_PASSWORD_PATH, { email });
  }

  resetPassword(data) {
    return this.http.post(AuthenticationService.API_RESET_PASSWORD_PATH, data);
  }

  getUserDetails(generatedToken?: string): JwtUser {
    let token;
    if (!generatedToken) {
      token = localStorage.getItem(AuthenticationService.USER_TOKEN_KEY);
    } else {
      token = generatedToken;
    }

    const userDetails = this.jwtHelper.decodeToken(token);
    return token ? new JwtUser(userDetails) : null;
  }

  userHasPermission(permission: string): boolean {
    if (!environment.permissionsRequired) {
      return true;
    }
    return this.getUserDetails()[permission];
  }

  get isAdmin(): boolean {
    const user = this.getUserDetails();
    return user?.role === Role.Admin;
  }

  get isUser(): boolean {
    const user = this.getUserDetails();
    return user?.role === Role.User;
  }
}
